import React, { useEffect, useState } from "react";
import { api } from "./api";

let configPromise = null;
export function storageConfig() {
  if (!configPromise) {
    configPromise = api.get("/files/config/storage").then((r) => r.data).catch(() => ({ direct_upload: false }));
  }
  return configPromise;
}

function putWithProgress(url, body, contentType, onProgress) {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    if (contentType) xhr.setRequestHeader("Content-Type", contentType);
    xhr.upload.onprogress = (e) => e.lengthComputable && onProgress && onProgress(e.loaded, e.total);
    xhr.onload = () => (xhr.status >= 200 && xhr.status < 300 ? resolve(xhr.getResponseHeader("ETag")) : reject(new Error(`PUT ${xhr.status}`)));
    xhr.onerror = () => reject(new Error("network"));
    xhr.send(body);
  });
}

async function directUpload(file, meta, onProgress) {
  const ct = file.type || "application/octet-stream";
  const { data: s } = await api.post("/files/upload-session", { filename: file.name, content_type: ct, size: file.size, ...meta });
  try {
    if (s.mode === "single") {
      await putWithProgress(s.upload_url, file, s.content_type, (l, t) => onProgress && onProgress(l / t));
      return (await api.post(`/files/upload-session/${s.session_id}/complete`, {})).data;
    }
    const parts = [];
    let done = 0;
    for (let n = 1; n <= s.parts_total; n++) {
      const { data } = await api.post(`/files/upload-session/${s.session_id}/parts`, { part_numbers: [n] });
      const chunk = file.slice((n - 1) * s.part_size, n * s.part_size);
      const etag = await putWithProgress(data.urls[String(n)], chunk, null, (l) => onProgress && onProgress((done + l) / file.size));
      done += chunk.size;
      parts.push({ PartNumber: n, ETag: etag });
    }
    return (await api.post(`/files/upload-session/${s.session_id}/complete`, { parts })).data;
  } catch (e) {
    api.delete(`/files/upload-session/${s.session_id}`).catch(() => {});
    throw e;
  }
}

// Legacy path (R2 disabled): identical to the historical POST /upload call.
async function legacyUpload(file, meta) {
  const fd = new FormData();
  fd.append("file", file);
  Object.entries(meta).forEach(([k, v]) => v !== undefined && fd.append(k, v));
  return (await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } })).data;
}

export async function uploadFile(file, meta = { entity_type: "document" }, onProgress) {
  const cfg = await storageConfig();
  return cfg.direct_upload ? directUpload(file, meta, onProgress) : legacyUpload(file, meta);
}

// Legacy files: fetched through the authenticated API client (cookie + Bearer) as a blob.
// R2 files: short-lived presigned GET (unchanged).
export async function fileObjectUrl(rec) {
  if (rec.storage_provider === "s3") return (await api.get(`/files/${rec.id}/download-url`)).data.url;
  const { data } = await api.get(`/files/${rec.id}`, { responseType: "blob" });
  return URL.createObjectURL(new Blob([data], { type: rec.content_type || data.type }));
}

// Thumbnails: lazy (only when visible) and throttled, so they never starve user actions (open/download).
const THUMB_CONCURRENCY = 3;
let thumbActive = 0;
const thumbQueue = [];
function thumbSlot() {
  return new Promise((resolve) => {
    const run = () => { thumbActive++; resolve(() => { thumbActive--; const next = thumbQueue.shift(); if (next) next(); }); };
    thumbActive < THUMB_CONCURRENCY ? run() : thumbQueue.push(run);
  });
}

export function AuthThumb({ rec, className }) {
  const [src, setSrc] = useState(null);
  const ref = React.useRef(null);
  useEffect(() => {
    let url, cancelled = false;
    const el = ref.current;
    const load = async () => {
      const release = await thumbSlot();
      try { if (!cancelled) { url = await fileObjectUrl(rec); if (!cancelled) setSrc(url); } }
      catch { /* keep placeholder */ }
      finally { release(); }
    };
    const io = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) { io.disconnect(); load(); } }, { rootMargin: "200px" });
    if (el) io.observe(el); else load();
    return () => { cancelled = true; io.disconnect(); if (url && url.startsWith("blob:")) URL.revokeObjectURL(url); };
  }, [rec.id]); // eslint-disable-line react-hooks/exhaustive-deps
  return src ? <img src={src} alt="" className={className} data-testid={`doc-thumb-${rec.id}`} /> : <div ref={ref} className={className} />;
}

function renderInWindow(win, url, rec) {
  const name = (rec.original_filename || "").replace(/[<>&"]/g, "");
  const ct = rec.content_type || "";
  const body = ct.startsWith("image/")
    ? `<img src="${url}" style="max-width:100%;max-height:100vh;display:block;margin:auto">`
    : `<embed src="${url}" type="${ct || "application/octet-stream"}" style="width:100vw;height:100vh;border:0">`;
  win.document.open();
  win.document.write(`<!doctype html><html><head><title>${name}</title><style>html,body{margin:0;background:#111}</style></head><body>${body}</body></html>`);
  win.document.close();
}

export async function openFile(rec) {
  // Open synchronously within the user gesture (popup blockers), then fill it once the file is ready.
  const win = window.open("", "_blank");
  try {
    const url = await fileObjectUrl(rec);
    if (!win) return window.open(url, "_blank", "noopener");
    if (url.startsWith("blob:")) renderInWindow(win, url, rec); else win.location.href = url;
  } catch (e) {
    if (win) win.close();
    throw e;
  }
}

export async function downloadFile(rec) {
  const url = await fileObjectUrl(rec);
  const a = document.createElement("a");
  a.href = url; a.download = rec.original_filename || "file"; a.rel = "noopener";
  document.body.appendChild(a); a.click(); a.remove();
  if (url.startsWith("blob:")) setTimeout(() => URL.revokeObjectURL(url), 60000);
}
